import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Property, Auction } from '../../types';
import { useAppStore } from '../../store/appStore';
import { client } from '../../api/client';

interface DateSlot {
  date: string;
  time: string;
}

interface SchedulingFormProps {
  property: Property | Auction;
  onClose: () => void;
  onSuccess?: () => void;
}

// Helper to format date as YYYY-MM-DD
function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Day names for display
const DAY_NAMES = {
  sr: ['Ned', 'Pon', 'Uto', 'Sre', 'Čet', 'Pet', 'Sub'],
  en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
};

export function SchedulingForm({ property, onClose, onSuccess }: SchedulingFormProps) {
  const { isAuthenticated, language } = useAppStore();
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  // Owner's available slots
  const [availableSlots, setAvailableSlots] = useState<DateSlot[]>([]);
  const [bookedSlots, setBookedSlots] = useState<string[]>([]);

  // Load owner's availability
  useEffect(() => {
    async function loadOwnerSlots() {
      try {
        const res = await client.api.fetch(`/api/scheduling/owner-slots?property_id=${property.id}`);
        const data = await res.json();
        if (data.success) {
          setAvailableSlots(data.date_slots || []);
          setBookedSlots(data.booked_slots || []);
        }
      } catch (err) {
        console.error('Failed to load owner slots:', err);
      } finally {
        setLoading(false);
      }
    }
    loadOwnerSlots();
  }, [property.id]);

  // Get unique available dates
  const availableDates = useMemo(() => {
    const dates = new Set<string>();
    const today = formatDate(new Date());
    
    availableSlots.forEach(slot => {
      // Only include future dates
      if (slot.date > today) {
        // Check if this slot is not booked
        const isBooked = bookedSlots.some(booked => 
          booked.startsWith(`${slot.date} ${slot.time}`) || 
          booked.startsWith(`${slot.date}T${slot.time}`)
        );
        if (!isBooked) {
          dates.add(slot.date);
        }
      }
    });
    
    return Array.from(dates).sort();
  }, [availableSlots, bookedSlots]);

  // Get available times for selected date
  const availableTimes = useMemo(() => {
    if (!date) return [];
    
    const times: string[] = [];
    availableSlots.forEach(slot => {
      if (slot.date === date) {
        // Check if not booked
        const isBooked = bookedSlots.some(booked => 
          booked.startsWith(`${slot.date} ${slot.time}`) || 
          booked.startsWith(`${slot.date}T${slot.time}`)
        );
        if (!isBooked) {
          times.push(slot.time);
        }
      }
    });
    
    return times.sort();
  }, [date, availableSlots, bookedSlots]);

  // Reset time when date changes
  useEffect(() => {
    setTime('');
  }, [date]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      setError(language === 'sr' ? 'Morate biti prijavljeni da biste zakazali obilazak.' : 'You must be logged in to schedule a visit.');
      return;
    }
    
    if (!date || !time) {
      setError(language === 'sr' ? 'Izaberite datum i vreme.' : 'Please select date and time.');
      return;
    }
    
    setSending(true);
    setError(null);
    
    try {
      const res = await client.api.fetch('/api/scheduling', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          propertyId: parseInt(property.id),
          scheduledDate: date,
          scheduledTime: time,
          phone,
          message
        })
      });
      
      const data = await res.json();
      
      if (data.success) {
        setSuccess(true);
        setTimeout(() => {
          onSuccess?.();
          onClose();
        }, 2000);
      } else {
        setError(data.error || (language === 'sr' ? 'Greška pri zakazivanju' : 'Error scheduling visit'));
      }
    } catch (err) {
      setError(language === 'sr' ? 'Greška pri zakazivanju. Pokušajte ponovo.' : 'Error scheduling visit. Please try again.');
    } finally {
      setSending(false);
    }
  };

  // Format date for display
  const formatDisplayDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const dayName = DAY_NAMES[language][d.getDay()];
    const day = d.getDate();
    const month = d.getMonth() + 1;
    return `${dayName}, ${day}.${month}.`;
  };
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
            {language === 'sr' ? 'Zakaži obilazak' : 'Schedule Visit'}
          </h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full transition-colors"
          >
            <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="py-8 flex flex-col items-center justify-center">
              <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mb-4" />
              <p className="text-slate-600 dark:text-slate-400">
                {language === 'sr' ? 'Učitavanje dostupnih termina...' : 'Loading available slots...'}
              </p>
            </div>
          ) : success ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h4 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                {language === 'sr' ? 'Zahtev je poslat!' : 'Request Sent!'}
              </h4>
              <p className="text-slate-600 dark:text-slate-400">
                {language === 'sr' 
                  ? 'Vlasnik oglasa će vam potvrditi termin.'
                  : 'The listing owner will confirm your appointment.'}
              </p>
            </div>
          ) : availableDates.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h4 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                {language === 'sr' ? 'Nema dostupnih termina' : 'No Available Slots'}
              </h4>
              <p className="text-slate-600 dark:text-slate-400 text-sm">
                {language === 'sr' 
                  ? 'Vlasnik još nije definisao dostupne termine. Pokušajte da ga kontaktirate direktno.'
                  : 'The owner has not set available times yet. Try contacting them directly.'}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Property info */}
              <div className="flex gap-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                {property.images.length > 0 && (
                  <img 
                    src={property.images[0]} 
                    alt={property.title}
                    className="w-16 h-16 object-cover rounded-lg"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-slate-900 dark:text-white truncate">{property.title}</h4>
                  <p className="text-sm text-slate-600 dark:text-slate-400">{property.location.city}</p>
                </div>
              </div>
              
              {/* Date Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {language === 'sr' ? 'Izaberite datum' : 'Select Date'} *
                </label>
                <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto p-1">
                  {availableDates.map(d => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setDate(d)}
                      className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                        date === d
                          ? 'bg-amber-500 text-white'
                          : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                      }`}
                    >
                      {formatDisplayDate(d)}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Time Selection */}
              {date && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {language === 'sr' ? 'Izaberite vreme' : 'Select Time'} *
                  </label>
                  {availableTimes.length === 0 ? (
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {language === 'sr' ? 'Svi termini za ovaj dan su zauzeti.' : 'All slots for this day are taken.'}
                    </p>
                  ) : (
                    <div className="grid grid-cols-4 gap-2">
                      {availableTimes.map(t => (
                        <button
                          key={t}
                          type="button"
                          onClick={() => setTime(t)}
                          className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                            time === t
                              ? 'bg-amber-500 text-white'
                              : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                          }`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {language === 'sr' ? 'Kontakt telefon' : 'Contact Phone'}
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  placeholder={language === 'sr' ? '06X XXX XXXX' : '06X XXX XXXX'}
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
              
              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {language === 'sr' ? 'Dodatna napomena' : 'Additional Note'}
                </label>
                <textarea
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  rows={2}
                  placeholder={language === 'sr' 
                    ? 'Npr. imam psa, treba mi parking u blizini...'
                    : 'E.g. I have a dog, need parking nearby...'}
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
                />
              </div>
              
              {/* Error */}
              {error && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}
              
              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-medium rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                >
                  {language === 'sr' ? 'Otkaži' : 'Cancel'}
                </button>
                <button
                  type="submit"
                  disabled={sending || !date || !time}
                  className="flex-1 px-4 py-2 bg-amber-500 text-white font-medium rounded-lg hover:bg-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {sending ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      {language === 'sr' ? 'Šalje se...' : 'Sending...'}
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      {language === 'sr' ? 'Zakaži' : 'Schedule'}
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
